import { useCallback, useEffect, useState } from 'react';
import { Api } from '../lib/api';
import { navigate } from '../lib/router';
import { Logo } from '../lib/ui';
import { TopbarUser, useSession } from '../App';

const ROLE_LABEL: Record<string, string> = { owner: 'Vlasnik', admin: 'Administrator', editor: 'Urednik', viewer: 'Pregled' };

export function AdminPage() {
  const { me } = useSession();
  const wsAdmin = me.workspace.role === 'owner' || me.workspace.role === 'admin';
  return (
    <div>
      <header className="topbar">
        <button className="btn ghost sm" onClick={() => navigate('/')}>
          <Logo size={18} /> ←
        </button>
        <strong>Administracija</strong>
        <div className="spacer" />
        <TopbarUser />
      </header>
      <main className="dash" style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
        {me.user.isSystemAdmin && <SystemSettings />}
        {wsAdmin ? <Members /> : <p className="muted">Članove radionice uređuju vlasnik i administratori.</p>}
      </main>
    </div>
  );
}

function SystemSettings() {
  const [s, setS] = useState<{ publicRegistration: boolean; mail: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    Api.adminSettings().then(setS, (e) => setError((e as Error).message));
  }, []);
  return (
    <section className="card" style={{ padding: 16, boxShadow: 'none' }}>
      <h2>Sistem</h2>
      {error && <p className="error-text">{error}</p>}
      {s && (
        <>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
            <input
              type="checkbox"
              checked={s.publicRegistration}
              onChange={async (e) => {
                try {
                  const r = await Api.setAdminSettings(e.target.checked);
                  setS({ ...s, publicRegistration: r.publicRegistration });
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            />
            <span>
              <strong>Javna registracija</strong> — na stranici za prijavu se pojavljuje „Napravi nalog”; svaki novi korisnik dobija svoju praznu radionicu.
            </span>
          </label>
          <p className="faint" style={{ fontSize: 12, margin: '8px 0 0 26px' }}>
            {s.mail === 'smtp'
              ? 'Email je podešen.'
              : 'Email (SMTP) nije podešen, pa se adresa pri registraciji ne potvrđuje mailom. Uključite registraciju samo kad vam treba, pa je ponovo isključite; za saradnike u svojoj radionici koristite pozivnice ispod.'}
          </p>
        </>
      )}
    </section>
  );
}

function Members() {
  const { me } = useSession();
  const ws = me.workspace;
  const [members, setMembers] = useState<{ userId: string; email: string; name: string; role: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [link, setLink] = useState<{ url: string; sent: boolean } | null>(null);
  const load = useCallback(() => Api.members(ws.id).then(setMembers, (e) => setError((e as Error).message)), [ws.id]);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <section className="card" style={{ padding: 16, boxShadow: 'none' }}>
      <h2>Članovi radionice „{ws.name}”</h2>
      {error && <p className="error-text">{error}</p>}
      <table className="grid" style={{ marginTop: 10 }}>
        <thead>
          <tr>
            <th>Ime</th>
            <th>Email</th>
            <th>Uloga</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.userId}>
              <td>{m.name}</td>
              <td>{m.email}</td>
              <td>
                {m.userId === me.user.id ? (
                  ROLE_LABEL[m.role]
                ) : (
                  <select className="input" style={{ height: 24 }} value={m.role} onChange={(e) => void act(() => Api.setRole(ws.id, m.userId, e.target.value))}>
                    {Object.entries(ROLE_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                )}
              </td>
              <td>
                {m.userId !== me.user.id && (
                  <button className="btn sm ghost danger" onClick={() => confirm(`Ukloniti ${m.name} iz radionice?`) && void act(() => Api.removeMember(ws.id, m.userId))}>
                    Ukloni
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 style={{ marginTop: 16 }}>Pozovi korisnika</h3>
      <form
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginTop: 6 }}
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            const r = await Api.invite(ws.id, email.trim(), role);
            setLink({ url: r.link, sent: r.mailSent });
            setEmail('');
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        <label className="field" style={{ marginBottom: 0, flex: '1 1 240px' }}>
          <span>Email</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>Uloga</span>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="editor">Urednik</option>
            <option value="viewer">Pregled</option>
            {me.workspace.role === 'owner' && <option value="admin">Administrator</option>}
          </select>
        </label>
        <button className="btn primary">Napravi pozivnicu</button>
      </form>
      {link && (
        <div style={{ marginTop: 10 }}>
          <p className={link.sent ? 'ok-text' : 'muted'}>{link.sent ? 'Pozivnica je poslana mailom. Link (važi 7 dana):' : 'Email nije podešen — pošaljite ovaj link osobi (važi 7 dana, jednokratan):'}</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input mono" style={{ flex: 1 }} readOnly value={link.url} onFocus={(e) => e.target.select()} />
            <button className="btn" onClick={() => void navigator.clipboard?.writeText(link.url)}>
              Kopiraj
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
